"use client";

import { useState } from "react";

import { usePrefersDark } from "@/lib/usePrefersDark";

export type AnnualEvapotranspirationRow = {
  kind: "annual_et";
  period: "historical_adjusted" | "2050s" | "2080s";
  period_years: string;
  median_mmyr: number;
  p10_mmyr?: number;
  p90_mmyr?: number;
};

export type DroughtIndicatorRow = {
  kind: "drought_indicator";
  indicator: "dry_days" | "dry_spells";
  label: string;
  unit: string;
  historical_adjusted: number;
  value_2050s_p10: number;
  value_2050s_median: number;
  value_2050s_p90: number;
  value_2080s_p10: number;
  value_2080s_median: number;
  value_2080s_p90: number;
};

export type LongTermNarrativeRow = {
  kind: "long_term_narrative";
  section: string;
  page: number;
  verbatim: string;
};

type Props = {
  rows: AnnualEvapotranspirationRow[];
  drySpells: DroughtIndicatorRow | undefined;
  height?: number;
};

const VIEW_W = 1000;
const PAD_TOP = 56;
const PAD_BOTTOM = 84;
const PAD_LEFT = 80;
const PAD_RIGHT = 156;

const PERIOD_ORDER: AnnualEvapotranspirationRow["period"][] = [
  "historical_adjusted",
  "2050s",
  "2080s",
];

const PERIOD_LABEL: Record<AnnualEvapotranspirationRow["period"], string> = {
  historical_adjusted: "1981–2010",
  "2050s": "2050s",
  "2080s": "2080s",
};

/**
 * Visual-first drought chart: two trajectories on the same canvas
 * indexed to the historical baseline (= 100). Evapotranspiration (ET)
 * climbs in alarm coral; dry-spell frequency stays nearly flat in
 * neutral. The eye reads the divergence directly: same dry-spell
 * count, but each dry day takes more water out of the soil. Values
 * are labelled inline at each point so the absolute numbers (mm/yr,
 * dry spells/yr) survive the % framing.
 */
export function ClimateEvapotranspirationBands({
  rows,
  drySpells,
  height = 420,
}: Props) {
  const isDark = usePrefersDark();
  const [hover, setHover] = useState<string | null>(null);

  const byPeriod = new Map(rows.map((r) => [r.period, r]));
  const ordered = PERIOD_ORDER.map((k) => byPeriod.get(k)).filter(
    (r): r is AnnualEvapotranspirationRow => Boolean(r),
  );
  const historical = byPeriod.get("historical_adjusted");

  // Index every value to the historical-baseline median = 100 so the
  // two metrics land on a comparable vertical scale and the divergence
  // is the visual story.
  const etIndex = (v: number) =>
    historical && historical.median_mmyr > 0
      ? (v / historical.median_mmyr) * 100
      : 100;
  const dsIndex = (v: number) =>
    drySpells && drySpells.historical_adjusted > 0
      ? (v / drySpells.historical_adjusted) * 100
      : 100;

  // Render the dry-spell trajectory only when the indicator row is
  // present in the artefact. Without it the divergence story can't be
  // told, but the ET trajectory is still meaningful on its own — the
  // chart degrades to a single coral series rather than rendering
  // NaN labels and broken aria text.
  const showDry = drySpells !== undefined;

  type Point = {
    period: AnnualEvapotranspirationRow["period"];
    et: number;
    etP10?: number;
    etP90?: number;
    dryMed?: number;
    dryP10?: number;
    dryP90?: number;
  };
  const points: Point[] = ordered.map((r) => {
    const ds = drySpells
      ? r.period === "historical_adjusted"
        ? {
            med: drySpells.historical_adjusted,
            p10: drySpells.historical_adjusted,
            p90: drySpells.historical_adjusted,
          }
        : r.period === "2050s"
          ? {
              med: drySpells.value_2050s_median,
              p10: drySpells.value_2050s_p10,
              p90: drySpells.value_2050s_p90,
            }
          : {
              med: drySpells.value_2080s_median,
              p10: drySpells.value_2080s_p10,
              p90: drySpells.value_2080s_p90,
            }
      : undefined;
    return {
      period: r.period,
      et: r.median_mmyr,
      etP10: r.p10_mmyr,
      etP90: r.p90_mmyr,
      dryMed: ds?.med,
      dryP10: ds?.p10,
      dryP90: ds?.p90,
    };
  });

  // Y-domain: index space. Bounds are derived so the largest projected
  // P90 (ET) and the floor (the smaller of dry-spell P10 and 100) both
  // sit cleanly inside the plot.
  const indexValues: number[] = [100];
  for (const pt of points) {
    indexValues.push(etIndex(pt.et));
    if (pt.etP10 !== undefined) indexValues.push(etIndex(pt.etP10));
    if (pt.etP90 !== undefined) indexValues.push(etIndex(pt.etP90));
    if (pt.dryMed !== undefined) indexValues.push(dsIndex(pt.dryMed));
    if (pt.dryP10 !== undefined) indexValues.push(dsIndex(pt.dryP10));
    if (pt.dryP90 !== undefined) indexValues.push(dsIndex(pt.dryP90));
  }
  const minIdx = Math.min(...indexValues);
  const maxIdx = Math.max(...indexValues);
  const yMin = Math.floor((minIdx - 4) / 5) * 5;
  const yMax = Math.ceil((maxIdx + 4) / 5) * 5;

  const VIEW_H = height;
  const plotTop = PAD_TOP;
  const plotBottom = VIEW_H - PAD_BOTTOM;
  const plotLeft = PAD_LEFT;
  const plotRight = VIEW_W - PAD_RIGHT;
  const plotW = plotRight - plotLeft;
  const plotH = plotBottom - plotTop;

  const yToPx = (v: number) =>
    plotBottom - ((v - yMin) / (yMax - yMin)) * plotH;

  const slotW = plotW / Math.max(points.length - 1, 1);
  const xForI = (i: number) =>
    points.length === 1 ? plotLeft + plotW / 2 : plotLeft + slotW * i;

  const ALARM = "var(--coral)";
  const NEUTRAL = isDark ? "rgba(213,213,208,0.6)" : "rgba(58,58,55,0.55)";
  const NEUTRAL_FILL = isDark ? "rgba(213,213,208,0.16)" : "rgba(58,58,55,0.12)";
  const ALARM_FILL = "var(--coral)";

  const baselineY = yToPx(100);

  const etPath = points
    .map((pt, i) => `${i === 0 ? "M" : "L"}${xForI(i)},${yToPx(etIndex(pt.et))}`)
    .join(" ");
  const dryPath = showDry
    ? points
        .filter((pt): pt is Point & { dryMed: number } => pt.dryMed !== undefined)
        .map(
          (pt, i) =>
            `${i === 0 ? "M" : "L"}${xForI(i)},${yToPx(dsIndex(pt.dryMed))}`,
        )
        .join(" ")
    : "";

  return (
    <div className="cs-chart">
      <svg
        viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
        className="w-full h-auto"
        role="img"
        aria-label="Annual evapotranspiration and dry-spell frequency at the Calgary International Airport ECCC station: ET rises through the 2050s and 2080s while dry-spell count stays essentially unchanged. Both series indexed to the 1981–2010 baseline."
      >
        {/* Legend keys, top-left of the plot. */}
        <g>
          <rect
            x={plotLeft}
            y={plotTop - 38}
            width={12}
            height={4}
            fill={ALARM}
            rx={1}
          />
          <text
            x={plotLeft + 18}
            y={plotTop - 32}
            fontSize={11.5}
            fontFamily="var(--font-feature)"
            fontWeight={700}
            fill="var(--ink)"
            dominantBaseline="central"
          >
            evapotranspiration (mm/yr)
          </text>
          {showDry ? (
            <>
              <rect
                x={plotLeft + 220}
                y={plotTop - 38}
                width={12}
                height={4}
                fill={NEUTRAL}
                rx={1}
              />
              <text
                x={plotLeft + 238}
                y={plotTop - 32}
                fontSize={11.5}
                fontFamily="var(--font-feature)"
                fontWeight={700}
                fill="var(--ink)"
                dominantBaseline="central"
              >
                dry spells (count/yr)
              </text>
            </>
          ) : null}
        </g>

        {/* Baseline reference line at index = 100. */}
        <line
          x1={plotLeft}
          x2={plotRight}
          y1={baselineY}
          y2={baselineY}
          stroke="var(--ink-3)"
          strokeDasharray="3 5"
          strokeWidth={1}
          opacity={0.6}
        />
        <text
          x={plotRight + 8}
          y={baselineY}
          fontSize={11}
          fontFamily="var(--font-feature)"
          fontWeight={600}
          fill="var(--ink-2)"
          dominantBaseline="central"
        >
          100
        </text>
        <text
          x={plotRight + 8}
          y={baselineY + 13}
          fontSize={9.5}
          fontFamily="var(--font-recursive)"
          fill="var(--ink-3)"
          dominantBaseline="central"
        >
          1981–2010 baseline
        </text>

        {/* Period guide rules + bottom labels. */}
        {points.map((pt, i) => (
          <g key={`guide-${pt.period}`}>
            <line
              x1={xForI(i)}
              x2={xForI(i)}
              y1={plotTop}
              y2={plotBottom}
              stroke="var(--rule)"
              strokeDasharray="2 6"
              strokeWidth={0.5}
              opacity={0.6}
            />
            <text
              x={xForI(i)}
              y={plotBottom + 18}
              fontSize={11.5}
              fontFamily="var(--font-recursive)"
              fontWeight={600}
              fill="var(--ink-2)"
              textAnchor="middle"
            >
              {PERIOD_LABEL[pt.period]}
            </text>
          </g>
        ))}

        {/* Dry-spell trajectory: P10–P90 ghost band drawn first, then
            the median line above it. The band is intentionally subtle —
            the visual story is "this stays flat", not the band width.
            Skipped entirely if `drySpells` was undefined. */}
        {showDry && dryPath && points.length >= 2 ? (
          <>
            <path
              d={[
                `M${xForI(0)},${yToPx(dsIndex(points[0].dryP90 ?? points[0].dryMed ?? 0))}`,
                ...points
                  .slice(1)
                  .map(
                    (pt, i) =>
                      `L${xForI(i + 1)},${yToPx(dsIndex(pt.dryP90 ?? pt.dryMed ?? 0))}`,
                  ),
                ...points
                  .slice()
                  .reverse()
                  .map(
                    (pt, i) =>
                      `L${xForI(points.length - 1 - i)},${yToPx(dsIndex(pt.dryP10 ?? pt.dryMed ?? 0))}`,
                  ),
                "Z",
              ].join(" ")}
              fill={NEUTRAL_FILL}
              stroke="none"
            />
            <path
              d={dryPath}
              fill="none"
              stroke={NEUTRAL}
              strokeWidth={2.2}
              strokeLinecap="round"
            />
          </>
        ) : null}

        {/* ET trajectory: P10–P90 alarm band then the median line. */}
        {points.length >= 2 &&
        points[1].etP10 !== undefined &&
        points[1].etP90 !== undefined ? (
          <>
            <path
              d={[
                `M${xForI(0)},${yToPx(etIndex(points[0].et))}`,
                ...points
                  .slice(1)
                  .map(
                    (pt, i) =>
                      `L${xForI(i + 1)},${yToPx(etIndex(pt.etP90 ?? pt.et))}`,
                  ),
                ...points
                  .slice()
                  .reverse()
                  .map((pt, i) => {
                    const idx = points.length - 1 - i;
                    return `L${xForI(idx)},${yToPx(etIndex(pt.etP10 ?? pt.et))}`;
                  }),
                "Z",
              ].join(" ")}
              fill={ALARM_FILL}
              opacity={0.16}
              stroke="none"
            />
            <path
              d={etPath}
              fill="none"
              stroke={ALARM}
              strokeWidth={2.6}
              strokeLinecap="round"
            />
          </>
        ) : (
          <path
            d={etPath}
            fill="none"
            stroke={ALARM}
            strokeWidth={2.6}
            strokeLinecap="round"
          />
        )}

        {/* Period markers + inline value labels. ET above the line in
            absolute mm/yr; dry-spells below in count/yr. Each marker
            is its own keyboard focus target. */}
        {points.map((pt, i) => {
          const xx = xForI(i);
          const etY = yToPx(etIndex(pt.et));
          const etPct = Math.round(etIndex(pt.et) - 100);
          const etHover = hover === `et-${pt.period}`;
          const dryHover = hover === `dry-${pt.period}`;
          const isLast = i === points.length - 1;
          // Anchor the inline labels just to the right of every point
          // (the last-point case used to need a different anchor; the
          // period guides are sparse enough that one rule covers all).
          const labelX = isLast ? xx + 12 : xx + 10;
          const dryY =
            pt.dryMed !== undefined ? yToPx(dsIndex(pt.dryMed)) : 0;
          const dryPct =
            pt.dryMed !== undefined
              ? Math.round(dsIndex(pt.dryMed) - 100)
              : 0;
          return (
            <g key={`pts-${pt.period}`}>
              <g
                tabIndex={0}
                role="img"
                aria-label={`${PERIOD_LABEL[pt.period]} evapotranspiration: ${pt.et} mm per year${etPct === 0 ? "" : etPct > 0 ? `, ${etPct}% above the 1981–2010 baseline` : `, ${Math.abs(etPct)}% below the 1981–2010 baseline`}`}
                onMouseEnter={() => setHover(`et-${pt.period}`)}
                onMouseLeave={() => setHover(null)}
                onFocus={() => setHover(`et-${pt.period}`)}
                onBlur={() => setHover(null)}
                style={{ outline: "none" }}
              >
                {etHover ? (
                  <circle
                    cx={xx}
                    cy={etY}
                    r={11}
                    fill="none"
                    stroke="var(--ink)"
                    strokeWidth={1.5}
                    pointerEvents="none"
                  />
                ) : null}
                <circle
                  cx={xx}
                  cy={etY}
                  r={5.5}
                  fill={ALARM}
                  stroke="var(--bg)"
                  strokeWidth={2}
                />
                <text
                  x={labelX}
                  y={etY - 6}
                  fontSize={12}
                  fontFamily="var(--font-feature)"
                  fontWeight={800}
                  fill="var(--ink)"
                  textAnchor="start"
                >
                  {pt.et}
                </text>
                <text
                  x={labelX}
                  y={etY + 8}
                  fontSize={10}
                  fontFamily="var(--font-recursive)"
                  fill="var(--ink-3)"
                  textAnchor="start"
                >
                  {etPct === 0
                    ? "mm/yr"
                    : `mm/yr · ${etPct > 0 ? "+" : ""}${etPct}%`}
                </text>
              </g>
              {pt.dryMed !== undefined ? (
                <g
                  tabIndex={0}
                  role="img"
                  aria-label={`${PERIOD_LABEL[pt.period]} dry spells: ${pt.dryMed} per year${dryPct === 0 ? ", essentially unchanged from the 1981–2010 baseline" : dryPct > 0 ? `, ${dryPct}% above the 1981–2010 baseline` : `, ${Math.abs(dryPct)}% below the 1981–2010 baseline`}`}
                  onMouseEnter={() => setHover(`dry-${pt.period}`)}
                  onMouseLeave={() => setHover(null)}
                  onFocus={() => setHover(`dry-${pt.period}`)}
                  onBlur={() => setHover(null)}
                  style={{ outline: "none" }}
                >
                  {dryHover ? (
                    <circle
                      cx={xx}
                      cy={dryY}
                      r={10}
                      fill="none"
                      stroke="var(--ink)"
                      strokeWidth={1.5}
                      pointerEvents="none"
                    />
                  ) : null}
                  <circle
                    cx={xx}
                    cy={dryY}
                    r={5}
                    fill={NEUTRAL}
                    stroke="var(--bg)"
                    strokeWidth={2}
                  />
                  <text
                    x={labelX}
                    y={dryY - 6}
                    fontSize={11.5}
                    fontFamily="var(--font-feature)"
                    fontWeight={700}
                    fill="var(--ink-2)"
                    textAnchor="start"
                  >
                    {pt.dryMed.toFixed(1)}
                  </text>
                  <text
                    x={labelX}
                    y={dryY + 8}
                    fontSize={10}
                    fontFamily="var(--font-recursive)"
                    fill="var(--ink-3)"
                    textAnchor="start"
                  >
                    /yr
                  </text>
                </g>
              ) : null}
            </g>
          );
        })}

        {/* Subtitle below the period labels. */}
        <text
          x={(plotLeft + plotRight) / 2}
          y={plotBottom + 50}
          fontSize={10.5}
          fontFamily="var(--font-recursive)"
          fill="var(--ink-3)"
          textAnchor="middle"
        >
          Penman-Monteith evapotranspiration · dry spells = ≥14 consecutive
          dry days · indexed to the 1981–2010 ECCC airport baseline
        </text>
      </svg>
    </div>
  );
}
